import cx from 'classnames';
import {isEmpty, map} from 'lodash';
import {Fragment, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {complianceAuditApi} from 'api';
import {
    Accordion, AccordionDetails, AccordionTitle, CopyText, DataWrapper, Icon, IconColor, IconName,
    Pagination, Table, TableBody, TableCell, TableRow, notifier
} from 'components';
import {UserContext} from 'context';
import {ExternalIntegrationPurpose} from 'enums';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {
    ComplianceAuditMessage, ComplianceAuditMessages, ComplianceDecision, ComplianceMessageAuthor, PaginationOptions,
    UserSummary
} from 'types';
import {dateUtils, formatDate} from 'utils';

import {ActivityFilters} from '../types';

import {complianceUtils} from './utils';

const T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY.COMPLIANCE;
const T_KEYS_STATISTICS_FAILED = TRANSLATION_KEYS.USER.ACTIVITY.ERROR.STATISTICS_FAILED;

const DISMISSED_DECISION_VIEW: {iconName: IconName, iconColor: IconColor, labelKey: string} = {
    iconName: 'info', iconColor: 'default', labelKey: T_KEYS.DECISION.DISMISSED
};

// Flagged bot messages are reworded by the server without an agent decision, so Accepted reads as Auto-replaced.
const AUTO_REPLACED_DECISION_VIEW: {iconName: IconName, iconColor: IconColor, labelKey: string} = {
    iconName: 'check-circle', iconColor: 'success', labelKey: T_KEYS.DECISION.AUTO_REPLACED
};

const DECISION_VIEWS: Record<ComplianceDecision, {iconName: IconName, iconColor: IconColor, labelKey: string}> = {
    [ComplianceDecision.Accepted]: {iconName: 'check-circle', iconColor: 'success', labelKey: T_KEYS.DECISION.ACCEPTED},
    [ComplianceDecision.Ignored]: {iconName: 'x-circle', iconColor: 'alert', labelKey: T_KEYS.DECISION.IGNORED}
};

const defaultPagination: PaginationOptions = {
    page: 1,
    pages: 1,
    count: 0,
    pageSize: 10
};

const defaultMessagesResponse: ComplianceAuditMessages = {
    items: [],
    total: 0
};

type Props = {
    filters: ActivityFilters | null;
    users: UserSummary[];
    purpose: ExternalIntegrationPurpose;
    isLoading: boolean;
    onToggleLoading: (value: boolean) => void;
};

export const ComplianceMessages = ({filters, users, purpose, isLoading, onToggleLoading}: Props) => {
    const {t} = useTranslation();
    const {settings: {common: {timeZone, dateFormat, timeFormat}}} = useContext(UserContext);
    const [pagination, setPagination] = useState<PaginationOptions>(defaultPagination);

    const dateTimeFormat = useMemo(() => dateUtils.getDateTimeFormat(dateFormat, timeFormat), [dateFormat, timeFormat]);

    const onError = useCallback(() => {
        notifier.error({title: t(T_KEYS_STATISTICS_FAILED)});
    }, [t]);

    const loadMessagesFn = useCallback(() => {
        if (!filters) {
            return Promise.resolve(defaultMessagesResponse);
        }
        return complianceAuditApi.getMessages(
            {...complianceUtils.toComplianceAuditFilters(filters), purpose},
            {
                skip: (pagination.page - 1) * pagination.pageSize,
                limit: pagination.pageSize
            }
        );
    }, [filters, purpose, pagination.page, pagination.pageSize]);

    const {send: loadMessages, data: messagesResponse, loading} = useApiData(loadMessagesFn, {onError});

    const messages = useMemo(() => messagesResponse?.items ?? [], [messagesResponse]);
    const totalItems = useMemo(() => messagesResponse?.total ?? 0, [messagesResponse]);

    useEffect(() => {
        if (isLoading !== loading) {
            onToggleLoading(loading);
        }
    }, [loading, isLoading, onToggleLoading]);

    const setPage = useCallback((value: number) => {
        setPagination((prev) => ({
            ...prev,
            page: value
        }));
    }, [setPagination]);

    const setPageSize = useCallback((value: number) => {
        setPagination((prev) => ({
            ...prev,
            pageSize: value
        }));
    }, [setPagination]);

    useEffect(() => {
        setPage(1);
    }, [setPage, filters, purpose, pagination.pageSize]);

    useEffect(() => {
        if (filters) {
            loadMessages();
        }
    }, [loadMessages, pagination.page, filters]);

    // Audio hints have no gate: the words were spoken as-is, and Accept/Ignore decisions do not exist —
    // the Transcriptions tab shows the offending quote and the correction or guidance.
    const isAudio = useMemo(() => purpose === ExternalIntegrationPurpose.AudioCompliance, [purpose]);

    const headers = useMemo(() => [
        {children: t(T_KEYS.MESSAGES_TABLE.COLUMNS.ID), className: 'tw-w-12'},
        {children: t(T_KEYS.MESSAGES_TABLE.COLUMNS.DATE), className: 'tw-w-44'},
        {children: t(T_KEYS.MESSAGES_TABLE.COLUMNS.PARTICIPANT), className: '-2xl:tw-w-56'},
        {children: t(T_KEYS.MESSAGES_TABLE.COLUMNS.RULE), className: '-2xl:tw-w-56'},
        {children: t(T_KEYS.MESSAGES_TABLE.COLUMNS.MESSAGE)},
        ...(isAudio ? [] : [{children: t(T_KEYS.MESSAGES_TABLE.COLUMNS.DECISION), className: 'tw-w-36'}])
    ], [t, isAudio]);

    const renderMessage = useCallback((item: ComplianceAuditMessage) => {
        if (isAudio) {
            return (
                <div className='tw-flex tw-flex-col tw-gap-1 tw-break-words'>
                    <div className={cx({
                        'tw-line-through tw-text-default-dimmed': !!item.correctedMessage,
                        'tw-ts-base-bold': !item.correctedMessage
                    })}
                    >
                        {item.originalMessage}
                    </div>
                    {item.correctedMessage
                        ? <div>{item.correctedMessage}</div>
                        : item.suggestedRewording && <div className='tw-text-default-dimmed'>{item.suggestedRewording}</div>}
                </div>
            );
        }
        // An undecided flagged bot message was still delivered: the gate fails open when no rewording came back.
        const sentAsIs = item.decision === ComplianceDecision.Ignored
            || (item.author === ComplianceMessageAuthor.Bot && !item.decision);
        return (
            <div className='tw-flex tw-flex-col tw-gap-1 tw-break-words'>
                <div className={cx({'tw-line-through tw-text-default-dimmed': !sentAsIs})}>{item.originalMessage}</div>
                {item.decision === ComplianceDecision.Accepted && <div>{item.finalMessage}</div>}
            </div>
        );
    }, [isAudio]);

    const renderDecision = useCallback((item: ComplianceAuditMessage) => {
        const isAutoReplaced = item.author === ComplianceMessageAuthor.Bot && item.decision === ComplianceDecision.Accepted;
        const {iconName, iconColor, labelKey} = isAutoReplaced
            ? AUTO_REPLACED_DECISION_VIEW
            : item.decision ? DECISION_VIEWS[item.decision] : DISMISSED_DECISION_VIEW;
        return (
            <div className='tw-flex tw-items-center tw-gap-2 tw-whitespace-nowrap'>
                <Icon name={iconName} color={iconColor} />
                {t(labelKey)}
            </div>
        );
    }, [t]);

    if (!filters) {
        return null;
    }

    return (
        <Fragment>
            {!isEmpty(messages) &&
                <div className='tw-flex tw-flex-wrap tw-items-center'>
                    <Pagination
                        maxItemsToDisplay={9}
                        className='tw-ml-auto tw-flex-1'
                        itemsClassName='tw-min-w-64 tw-justify-between sm:tw-justify-end'
                        rangeClassName='-sm:tw-hidden'
                        pageSelectorClassName='-sm:tw-hidden'
                        pageSize={pagination.pageSize}
                        pageNumber={pagination.page}
                        totalItems={totalItems}
                        onPageNumberChange={setPage}
                        onPageSizeChange={setPageSize}
                    />
                </div>}
            <div className='tw-my-2 tw-flex tw-flex-col tw-overflow-auto -md:tw-hidden'>
                <Table
                    loading={isLoading}
                    noData={isEmpty(messages)}
                    headers={headers}
                    className='tw-overflow-x-auto tw-min-w-256'
                >
                    <TableBody className='tw-text-default'>
                        {map(messages, (item) => (
                            <TableRow key={item.verdictId}>
                                <TableCell>
                                    <CopyText
                                        text={item.sessionId}
                                        iconOnly
                                        iconColor='default'
                                        copiedLabelOnly
                                    />
                                </TableCell>
                                <TableCell>{formatDate(item.createdAt, timeZone, dateTimeFormat)}</TableCell>
                                <TableCell ellipsis>{complianceUtils.getParticipantName(t, users, item)}</TableCell>
                                <TableCell>{item.rule}</TableCell>
                                <TableCell>{renderMessage(item)}</TableCell>
                                {!isAudio && <TableCell>{renderDecision(item)}</TableCell>}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            <div className='tw-my-2 md:tw-hidden'>
                <DataWrapper isLoading={isLoading} noData={isEmpty(messages)}>
                    <div className='tw-grid tw-min-h-8'>
                        {map(messages, (item) => (
                            <Accordion key={item.verdictId} className='tw-min-w-48 tw-mb-2 last:tw-mb-0'>
                                <AccordionTitle>
                                    <div className='tw-flex tw-items-center tw-gap-2'>
                                        <div className='tw-ts-h5-bold tw-flex-shrink tw-overflow-hidden tw-text-ellipsis'>
                                            {complianceUtils.getParticipantName(t, users, item)}
                                        </div>
                                        {/* Fixed width so the decision icons/labels line up in one column across rows. */}
                                        {!isAudio &&
                                            <div className='tw-ml-auto tw-w-32 tw-shrink-0 -sm:tw-mr-2'>{renderDecision(item)}</div>}
                                    </div>
                                </AccordionTitle>
                                <AccordionDetails>
                                    <div className='tw-flex tw-flex-col tw-gap-2'>
                                        <div className='tw-flex tw-items-center tw-gap-1 tw-text-default-dimmed'>
                                            <Icon name='id' size='lg' />
                                            <span className='tw-overflow-hidden tw-text-ellipsis'>{item.sessionId}</span>
                                            <CopyText
                                                text={item.sessionId}
                                                iconOnly
                                                iconColor='default'
                                                copiedLabelOnly
                                            />
                                        </div>
                                        <div className='tw-text-default-dimmed'>
                                            {formatDate(item.createdAt, timeZone, dateTimeFormat)}
                                        </div>
                                        {item.rule && <div className='tw-ts-h5-bold'>{item.rule}</div>}
                                        {renderMessage(item)}
                                    </div>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </div>
                </DataWrapper>
            </div>
        </Fragment>
    );
};
